import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import type { DockWidget } from "@slumbr/app-sdk";

import { useGameChatStore } from "./game-chat-store";

function GameChatCompact() {
  const active = useGameChatStore((s) => s.active);
  const last = active?.messages[active.messages.length - 1];
  if (!active) return <span className="font-semibold">Game chat</span>;
  return (
    <span className="flex min-w-0 items-baseline gap-1.5">
      <span className="shrink-0 font-bold">{active.title}</span>
      {last && <span className="min-w-0 truncate text-[#8b93c7]">{last.sender}: {last.text}</span>}
    </span>
  );
}

function GameChatPanel() {
  const active = useGameChatStore((s) => s.active);
  const [draft, setDraft] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [active?.messages.length]);

  if (!active) return <p className="px-4 py-8 text-center text-sm text-[#8b93c7]">Join a game to chat.</p>;

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    active.send(text);
    setDraft("");
  };

  return (
    <div className="flex h-[52dvh] flex-col">
      <div className="min-h-0 flex-1 space-y-1.5 overflow-auto px-3 py-2">
        {active.messages.length === 0 ? (
          <p className="py-6 text-center text-xs text-[#8b93c7]">No messages yet.</p>
        ) : (
          active.messages.slice(-50).map((m) => (
            <div key={m.id} className={"flex " + (m.mine ? "justify-end" : "justify-start")}>
              <span className={"max-w-[82%] rounded-2xl px-3 py-1.5 text-sm " + (m.mine ? "bg-[#22d3ee] text-[#06121f]" : "bg-white/[0.06] text-[#e7e9ea]")}>
                {!m.mine && <span className="mr-1 text-[11px] font-black text-[#8b93c7]">{m.sender}</span>}
                {m.text}
              </span>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>
      <div className="flex items-center gap-2 border-t border-white/[0.06] p-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
          placeholder="Message the room…"
          className="min-w-0 flex-1 rounded-full bg-white/[0.05] px-3 py-2 text-sm text-white outline-none placeholder:text-[#536471]"
        />
        <button type="button" onClick={send} disabled={!draft.trim()} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#22d3ee] text-[#06121f] transition disabled:opacity-40">
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export const gameChatWidget: DockWidget = {
  id: "games.chat",
  appId: "games",
  title: "Game chat",
  icon: "🎮",
  contextual: true,
  isAvailable: () => useGameChatStore.getState().active != null,
  badge: () => {
    const u = useGameChatStore.getState().active?.unread ?? 0;
    return u > 0 ? u : undefined;
  },
  CompactUI: GameChatCompact,
  PanelUI: GameChatPanel,
};
